const BOOK_LIST_CACHE_KEY = "book-list-cache-v1";
const BOOK_LIST_PENDING_KEY = "book-list-pending-v1";

const EMPTY_STATUS = {
  bookmarks: false,
  finished: false,
  tbr: false
};

const EMPTY_LISTS = {
  bookmarks: { count: 0, items: [] },
  finished: { count: 0, items: [] },
  tbr: { count: 0, items: [] }
};

const BOOK_LIST_NAMES = ["bookmarks", "finished", "tbr"];

const readJson = (key, fallback) => {
  try {
    const raw = localStorage.getItem(key);
    return raw ? JSON.parse(raw) : fallback;
  } catch (error) {
    return fallback;
  }
};

const writeJson = (key, value) => {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch (error) {}
};

const getUserCacheKey = (userId) => String(Number(userId) || 0);
const getBookKey = (epubId) => String(Number(epubId) || 0);

const getCacheRoot = () => readJson(BOOK_LIST_CACHE_KEY, {});
const setCacheRoot = (next) => writeJson(BOOK_LIST_CACHE_KEY, next);

const getPendingRoot = () => readJson(BOOK_LIST_PENDING_KEY, []);
const setPendingRoot = (next) => writeJson(BOOK_LIST_PENDING_KEY, next);

const ensureUserCache = (root, userId) => {
  const userKey = getUserCacheKey(userId);
  if (!root[userKey]) {
    root[userKey] = {
      statusByBook: {},
      lists: {
        bookmarks: [],
        finished: [],
        tbr: []
      },
      summary: EMPTY_LISTS
    };
  }

  if (!root[userKey].statusByBook) {
    root[userKey].statusByBook = {};
  }

  if (!root[userKey].lists) {
    root[userKey].lists = {
      bookmarks: [],
      finished: [],
      tbr: []
    };
  }

  if (!root[userKey].summary) {
    root[userKey].summary = EMPTY_LISTS;
  }

  return root[userKey];
};

const dedupeById = (items = []) => {
  const map = new Map();
  items.forEach((item) => {
    if (!item?.id) return;
    map.set(Number(item.id), item);
  });
  return Array.from(map.values());
};

const sortByUpdatedAtDesc = (items = []) =>
  [...items].sort(
    (left, right) =>
      Number(new Date(right?.updated_at || right?.created_at || 0)) -
      Number(new Date(left?.updated_at || left?.created_at || 0))
  );

const summarizeLists = (lists) => ({
  bookmarks: {
    count: lists.bookmarks.length,
    items: lists.bookmarks
  },
  finished: {
    count: lists.finished.length,
    items: lists.finished
  },
  tbr: {
    count: lists.tbr.length,
    items: lists.tbr
  }
});

const buildListItem = ({
  book,
  listName,
  bookmarkNote = "",
  previousItem = null
}) => {
  const now = new Date().toISOString().replace("T", " ").slice(0, 19);
  const base = {
    id: Number(book?.id) || 0,
    title: book?.title || previousItem?.title || "",
    author: book?.author || previousItem?.author || "",
    summary: book?.summary || previousItem?.summary || "",
    updated_at: now,
    created_at: previousItem?.created_at || now
  };

  if (listName === "bookmarks") {
    return {
      ...base,
      note_text: bookmarkNote || previousItem?.note_text || ""
    };
  }

  if (listName === "finished") {
    return {
      ...base,
      started_at: previousItem?.started_at || now
    };
  }

  return base;
};

export const getCachedProfileLists = (userId) => {
  const root = getCacheRoot();
  const userCache = root[getUserCacheKey(userId)];
  return userCache?.summary || EMPTY_LISTS;
};

export const cacheProfileLists = (userId, lists) => {
  const root = getCacheRoot();
  const userCache = ensureUserCache(root, userId);

  const nextLists = {
    bookmarks: dedupeById(lists?.bookmarks?.items || []),
    finished: dedupeById(lists?.finished?.items || []),
    tbr: dedupeById(lists?.tbr?.items || [])
  };

  userCache.lists = nextLists;
  userCache.summary = {
    bookmarks: {
      count: Number(lists?.bookmarks?.count || nextLists.bookmarks.length),
      items: nextLists.bookmarks
    },
    finished: {
      count: Number(lists?.finished?.count || nextLists.finished.length),
      items: nextLists.finished
    },
    tbr: {
      count: Number(lists?.tbr?.count || nextLists.tbr.length),
      items: nextLists.tbr
    }
  };

  nextLists.bookmarks.forEach((item) => {
    const bookKey = getBookKey(item.id);
    userCache.statusByBook[bookKey] = {
      ...(userCache.statusByBook[bookKey] || EMPTY_STATUS),
      bookmarks: true,
      bookmark_note: item.note_text || userCache.statusByBook[bookKey]?.bookmark_note || ""
    };
  });
  nextLists.finished.forEach((item) => {
    const bookKey = getBookKey(item.id);
    userCache.statusByBook[bookKey] = {
      ...(userCache.statusByBook[bookKey] || EMPTY_STATUS),
      finished: true
    };
  });
  nextLists.tbr.forEach((item) => {
    const bookKey = getBookKey(item.id);
    userCache.statusByBook[bookKey] = {
      ...(userCache.statusByBook[bookKey] || EMPTY_STATUS),
      tbr: true
    };
  });

  setCacheRoot(root);
  return userCache.summary;
};

export const getCachedBookList = (userId, listName) => {
  const root = getCacheRoot();
  const userCache = root[getUserCacheKey(userId)];
  return Array.isArray(userCache?.lists?.[listName]) ? userCache.lists[listName] : [];
};

export const cacheBookList = (userId, listName, items) => {
  const root = getCacheRoot();
  const userCache = ensureUserCache(root, userId);
  userCache.lists[listName] = sortByUpdatedAtDesc(dedupeById(items || []));
  userCache.summary = summarizeLists(userCache.lists);

  userCache.lists[listName].forEach((item) => {
    const bookKey = getBookKey(item.id);
    const currentStatus = userCache.statusByBook[bookKey] || EMPTY_STATUS;
    userCache.statusByBook[bookKey] = {
      ...currentStatus,
      [listName]: true,
      ...(listName === "bookmarks"
        ? { bookmark_note: item.note_text || currentStatus.bookmark_note || "" }
        : {})
    };
  });

  setCacheRoot(root);
  return userCache.lists[listName];
};

export const getCachedBookListStatus = (userId, epubId) => {
  const root = getCacheRoot();
  const userCache = root[getUserCacheKey(userId)];
  const status = userCache?.statusByBook?.[getBookKey(epubId)];
  return status ? { ...EMPTY_STATUS, ...status } : null;
};

export const cacheBookListStatus = (userId, epubId, status) => {
  const root = getCacheRoot();
  const userCache = ensureUserCache(root, userId);
  const bookKey = getBookKey(epubId);
  userCache.statusByBook[bookKey] = {
    ...(userCache.statusByBook[bookKey] || EMPTY_STATUS),
    ...status
  };
  setCacheRoot(root);
  return userCache.statusByBook[bookKey];
};

export const applyBookListUpdate = ({
  userId,
  book,
  status,
  bookmarkNote = ""
}) => {
  const root = getCacheRoot();
  const userCache = ensureUserCache(root, userId);
  const bookKey = getBookKey(book?.id);
  const nextStatus = {
    ...(userCache.statusByBook[bookKey] || EMPTY_STATUS),
    ...EMPTY_STATUS,
    ...status,
    bookmark_note: bookmarkNote
  };

  userCache.statusByBook[bookKey] = nextStatus;

  BOOK_LIST_NAMES.forEach((listName) => {
    const currentItems = Array.isArray(userCache.lists[listName])
      ? [...userCache.lists[listName]]
      : [];
    const existingItem = currentItems.find((item) => Number(item.id) === Number(book?.id)) || null;

    if (nextStatus[listName]) {
      const nextItem = buildListItem({
        book,
        listName,
        bookmarkNote,
        previousItem: existingItem
      });
      const filtered = currentItems.filter((item) => Number(item.id) !== Number(book?.id));
      userCache.lists[listName] = sortByUpdatedAtDesc([nextItem, ...filtered]);
    } else {
      userCache.lists[listName] = currentItems.filter(
        (item) => Number(item.id) !== Number(book?.id)
      );
    }
  });

  userCache.summary = summarizeLists(userCache.lists);
  setCacheRoot(root);
  return {
    status: nextStatus,
    counts: {
      bookmarks: userCache.summary.bookmarks.count,
      finished: userCache.summary.finished.count,
      tbr: userCache.summary.tbr.count
    },
    lists: userCache.summary
  };
};

export const queueBookListUpdate = ({
  userId,
  book,
  status,
  bookmarkNote = ""
}) => {
  const pending = getPendingRoot();
  const key = `${getUserCacheKey(userId)}:${getBookKey(book?.id)}`;
  const nextItem = {
    key,
    userId: Number(userId) || 0,
    epubId: Number(book?.id) || 0,
    book,
    status,
    bookmarkNote
  };
  const next = pending.filter((item) => item.key !== key);
  next.push(nextItem);
  setPendingRoot(next);
};

export const flushPendingBookListUpdates = async (apiUrl) => {
  if (!navigator.onLine || !apiUrl) return;
  const pending = getPendingRoot();
  if (!pending.length) return;

  const rememberToken = localStorage.getItem("rememberToken") || "";
  const remaining = [];

  for (const item of pending) {
    try {
      const form = new FormData();
      form.append("uid", String(item.userId));
      form.append("epub_id", String(item.epubId));
      form.append("bookmarks", item.status?.bookmarks ? "1" : "0");
      form.append("finished", item.status?.finished ? "1" : "0");
      form.append("tbr", item.status?.tbr ? "1" : "0");
      form.append("bookmark_note", item.bookmarkNote || "");

      const response = await fetch(`${apiUrl}/save-book-lists.php`, {
        method: "POST",
        credentials: "include",
        headers: rememberToken ? { "X-Remember-Token": rememberToken } : undefined,
        body: form
      });
      const data = await response.json().catch(() => ({}));

      if (!response.ok || !data?.success) {
        remaining.push(item);
        continue;
      }

      applyBookListUpdate({
        userId: item.userId,
        book: item.book,
        status: data.status || item.status,
        bookmarkNote: item.bookmarkNote
      });
    } catch (error) {
      remaining.push(item);
    }
  }

  setPendingRoot(remaining);
};
